<script setup lang="ts">
import { onMounted, ref } from "vue";

import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import UserAvatar from "@/components/ui/UserAvatar.vue";
import { getUserFriendlyName, type ProjectDto } from "@/dto";
import { ProjectDetailSections, RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { formatDatetime } from "@/services/utils";

const { workspaceName: workspaceName, project } = defineProps<{
  workspaceName: string;
  project: ProjectDto;
}>();
const emit = defineEmits(["delete"]);

const api = useApi();
const userName = ref("");

function pluralize(word: string, count: number) {
  return count > 1 ? `${word}s` : word;
}

async function fetchUser() {
  const user = await api.fetchUser(project.creator_id);
  if (user) {
    userName.value = getUserFriendlyName(user);
  }
}

onMounted(async () => {
  await fetchUser();
});
</script>

<template>
  <div class="card project">
    <header>
      <div class="name text-base-medium">
        <span>{{ project.name }}</span>
      </div>

      <div class="actions">
        <DropdownButton icon-right="icon-more-vertical" kind="ghost">
          <DropdownButtonItem label="Delete" icon-left="icon-trash" @click="emit('delete')" />
        </DropdownButton>
      </div>
    </header>

    <hr />

    <div class="metadata text-base-regular">
      <div class="datum">
        <div class="name"><i class="icon icon-calendar" />Last update</div>
        <div class="value">
          {{ formatDatetime(project.created_at, false) }}
        </div>
      </div>

      <div class="datum">
        <div class="name"><i class="icon icon-user" />Created by</div>
        <div class="value">
          <UserAvatar :name="userName" />
        </div>
      </div>
    </div>

    <hr />

    <div class="counts">
      <i class="counts-icon icon icon-arrow-turn-right" />
      <div class="counts-wrapper text-base-regular">
        <div class="count estimator">
          <RouterLink
            :to="{
              name: RouteNames.ProjectDetail,
              params: {
                workspaceName: workspaceName,
                projectName: project.name,
                section: ProjectDetailSections.EstimatorReports,
              },
            }"
          >
            <i class="icon icon-cube" />
            <em>{{ project.estimator_report_count }}</em>
            {{ pluralize("estimator", project.estimator_report_count) }}
          </RouterLink>
        </div>

        <div class="count cross-validation">
          <RouterLink
            :to="{
              name: RouteNames.ProjectDetail,
              params: {
                workspaceName: workspaceName,
                projectName: project.name,
                section: ProjectDetailSections.CrossValidationReports,
              },
            }"
          >
            <i class="icon icon-playground" />
            <em>{{ project.cross_validation_report_count }}</em>
            {{ pluralize("cross validation", project.cross_validation_report_count) }}
          </RouterLink>
        </div>

        <div class="count comparison">
          <RouterLink
            :to="{
              name: RouteNames.ProjectDetail,
              params: {
                workspaceName: workspaceName,
                projectName: project.name,
                section: ProjectDetailSections.Comparisons,
              },
            }"
          >
            <i class="icon icon-scale" />
            <em>{{ project.comparison_count }}</em>
            {{ pluralize("comparison", project.comparison_count) }}
          </RouterLink>
        </div>
      </div>
    </div>

    <RouterLink
      class="fallback-link"
      :to="{
        name: RouteNames.ProjectDetail,
        params: {
          workspaceName: workspaceName,
          projectName: project.name,
          section: ProjectDetailSections.EstimatorReports,
        },
      }"
    />
  </div>
</template>

<style scoped>
.project {
  position: relative;
  padding: var(--numbers-4);
  background-color: var(--background-primary);
  gap: var(--numbers-6);
  transition: border-color var(--animation-duration) var(--animation-easing);

  &:hover {
    border-color: var(--border-primary);
  }

  header,
  .metadata,
  .counts {
    padding: var(--numbers-8);
  }

  header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    .name {
      --icon-size: 28px;

      position: relative;
      padding-left: calc(var(--icon-size) + var(--numbers-8));
      line-height: var(--icon-size);

      &::before {
        position: absolute;
        left: 0;
        overflow: hidden;
        width: var(--icon-size);
        height: var(--icon-size);
        border: solid var(--stroke-width-md) var(--border-tertiary);
        border-radius: var(--icon-size);
        background-color: var(--background-secondary);
        background-image: url("@/assets/images/skore-icon.svg");
        background-position: 60% 50%;
        background-repeat: no-repeat;
        background-size: 70%;
        content: "";
      }
    }

    .actions {
      z-index: 3;

      :deep(.skore-button) {
        --height: var(--numbers-24);

        padding: var(--numbers-8) var(--numbers-4);
        border-radius: var(--numbers-4);
      }
    }
  }

  hr {
    border: none;
    border-top: solid var(--stroke-width-md) var(--border-tertiary);
  }

  .metadata {
    display: flex;
    flex-direction: column;
    padding: var(--numbers-8);
    gap: var(--numbers-4);

    .datum {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      .name {
        color: var(--content-tertiary);

        .icon {
          padding-right: var(--numbers-6);
          vertical-align: text-top;
        }
      }

      .value {
        color: var(--content-primary);
      }
    }
  }

  .counts {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--numbers-16);

    .counts-icon {
      color: var(--content-tertiary);
    }

    .counts-wrapper {
      display: flex;
      flex-flow: row wrap;
      align-items: center;
      gap: var(--numbers-8);
    }

    .count {
      position: relative;
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 0 var(--numbers-4);
      border: solid var(--stroke-width-md) var(--border-tertiary);
      border-radius: var(--numbers-4);
      background-color: var(--background-secondary);
      color: var(--content-secondary);
      gap: var(--numbers-4);
      text-overflow: ellipsis;
      white-space: nowrap;

      i {
        margin-right: var(--numbers-4);
        vertical-align: text-bottom;
      }

      &.estimator i {
        color: var(--color-green-500);
      }

      &.cross-validation i {
        color: var(--color-blue-500);
      }

      &.comparison i {
        color: var(--color-purple-500);
      }

      em {
        color: var(--content-primary);
        font-style: normal;
      }
    }
  }

  a {
    z-index: 2;
    color: inherit;
    text-decoration: none;
  }

  a.fallback-link {
    z-index: 1;

    &::before {
      position: absolute;
      content: "";
      inset: calc(var(--numbers-12) * -1);
    }
  }
}
</style>
